import { COBFolio } from "creso-core/types";
import { addFullStopToSentence, convertToCurrency } from "creso-core/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  VisuallyHidden,
  ScrollArea,
  Button,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "web-design-system";
import { useMemo, useState } from "react";
import { AmcLogo } from "@core/components/logo/amc-logo";
import { useDownloadPdfReport } from "web-core/hooks";
import { getPdfDownloadUrl } from "web-core/utils";
import { handleError } from "@/utils/error";
import { ConditionalRenderer } from "@core/components/conditional-renderer";
import { Download, Loader2, Repeat2, Send } from "lucide-react";
import { useInvalidateCobBreakdown } from "creso-core/hooks";
import { CobStatusBadge } from "web-core";
import { useCob } from "../providers/cob-provider";
import { ConfirmCobTransit } from "./confirm-transit-dialog";

function FolioRow({ folio }: { folio: COBFolio }) {
  const { selectedClient } = useCob();

  if (!selectedClient) return null;

  return (
    <div className="grid grid-cols-12 items-center py-3">
      <div className="flex col-span-4 gap-2 items-center pl-8">
        <AmcLogo amc_code={folio.morningstar_amc_code} size="sm" />
        <span className="text-sm flex-1 min-w-0">
          Folio no: {folio.folio_number}
        </span>
      </div>
      <span className="text-sm shrink-0 text-right col-span-3">
        {convertToCurrency(folio.amount)}
      </span>
      <div className="col-span-3 justify-end flex items-center shrink-0">
        <CobStatusBadge
          status={folio.status}
          title={
            folio.status === "REJECTED"
              ? addFullStopToSentence(folio.remarks || "")
              : ""
          }
        />
        {folio.remarks && folio.status !== "REJECTED" && (
          <Tooltip>
            <TooltipTrigger>
              <Repeat2 className="ml-1 size-4 text-muted-foreground" />
            </TooltipTrigger>
            <TooltipContent className="max-w-60">
              {addFullStopToSentence(
                `This application was previously rejected: ${folio.remarks}`
              )}
            </TooltipContent>
          </Tooltip>
        )}
      </div>
      <div className="flex flex-row gap-1 justify-end pr-4 col-span-2 shrink-0">
        <ConfirmCobTransit client={selectedClient} folioId={[folio.folio]} />
      </div>
    </div>
  );
}

function BrokerGroup({ group }: { group: COBFolio[] }) {
  if (!group?.length) return null;

  const brokerCode = group[0].broker_code;
  const brokerName = group[0].broker_name;
  const totalAmount = group.reduce((acc, curr) => acc + Number(curr.amount), 0);

  return (
    <div className="flex flex-col rounded-lg">
      <div className="grid grid-cols-12 items-start justify-between bg-card px-4 py-3">
        <div className="flex flex-col col-span-4 gap-1">
          <span className="text-sm">{brokerName}</span>
          <span className="text-xs text-muted-foreground">{brokerCode}</span>
        </div>
        <span className="text-sm font-medium shrink-0 col-span-3 text-right">
          {convertToCurrency(totalAmount)}
        </span>
      </div>
      <div className="flex flex-col divide-y divide-border">
        {group?.map((folio) => (
          <FolioRow key={folio.folio_number} folio={folio} />
        ))}
      </div>
    </div>
  );
}

export function COBViewDetailsDialog() {
  const {
    selectedClient: client,
    setSelectedClient,
    initiateCobFormDownload,
  } = useCob();

  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadReport] = useDownloadPdfReport();

  const invalidateCobBreakdown = useInvalidateCobBreakdown();

  const handleDownload = async (foliosToDownload: COBFolio[]) => {
    try {
      setIsDownloading(true);
      await initiateCobFormDownload(client, foliosToDownload);
      await downloadReport(
        getPdfDownloadUrl(`/reports/clients/${client?.client_id}/cob`),
        `${client?.client_name}-change-of-broker.pdf`,
        {
          loading: "Generating change of broker form",
        }
      );
      invalidateCobBreakdown();
      setSelectedClient((prev) => {
        if (!prev) return prev;
        const newClient = { ...prev };
        newClient.folio_details = newClient.folio_details.map((fd) => {
          if (foliosToDownload.some((f) => f.folio === fd.folio)) {
            return { ...fd, status: "DOWNLOADED" };
          }
          return fd;
        });
        return newClient;
      });
    } catch (error) {
      handleError(error, "Failed to download change of broker form");
    } finally {
      setIsDownloading(false);
    }
  };

  const folioDetailsGroupedByBrokerCode = useMemo(
    () =>
      client?.folio_details.reduce(
        (acc, folio) => {
          acc[folio.broker_code] = acc[folio.broker_code] || [];
          acc[folio.broker_code].push(folio);
          return acc;
        },
        {} as Record<string, COBFolio[]>
      ),
    [client?.folio_details]
  );

  const rejectedFolios = useMemo(() => {
    return client?.folio_details?.filter(
      (folio) => folio.status === "REJECTED"
    );
  }, [client?.folio_details]);

  const downloadedFolios = useMemo(() => {
    return client?.folio_details?.filter(
      (folio) => folio.status === "DOWNLOADED"
    );
  }, [client?.folio_details]);

  if (!client || !folioDetailsGroupedByBrokerCode) return null;

  return (
    <Dialog open onOpenChange={(v) => !v && setSelectedClient(null)}>
      <DialogContent
        autoFocus={false}
        className="max-w-4xl gap-0 p-0 overflow-hidden"
      >
        <ScrollArea className="max-h-[75vh] pb-5">
          <VisuallyHidden>
            <DialogHeader>
              <DialogTitle>
                Change of Broker for {client.client_name}
              </DialogTitle>
              <DialogDescription>
                Folio details for {client.client_name}
              </DialogDescription>
            </DialogHeader>
          </VisuallyHidden>
          <div className="flex flex-col px-5">
            <div className="flex sticky top-0 z-2 flex-col bg-background py-5">
              <span className="text-lg font-semibold">
                Change of Broker for {client.client_name}
              </span>
              <span className="text-sm text-muted-foreground">
                External AUM:{" "}
                <span className="font-semibold">
                  {convertToCurrency(client.external_aum)}
                </span>
              </span>
            </div>
            <ConditionalRenderer condition={!!rejectedFolios?.length}>
              <div className="flex flex-row rounded-sm pl-4 bg-tag-red items-center">
                <span className="text-sm">
                  {rejectedFolios?.length} applications have been rejected
                </span>
                <Button
                  className="ml-auto"
                  disabled={isDownloading}
                  variant="ghost"
                  onClick={() => handleDownload(rejectedFolios as COBFolio[])}
                >
                  {isDownloading ? (
                    <>
                      <Loader2 className="animate-spin" />
                      Downloading...
                    </>
                  ) : (
                    <>
                      <Download />
                      Download again
                    </>
                  )}
                </Button>
              </div>
            </ConditionalRenderer>
            <ConditionalRenderer
              condition={!!downloadedFolios?.length && !rejectedFolios?.length}
            >
              <div className="flex flex-row rounded-sm pl-4 bg-tag-blue items-center">
                <span className="text-sm mr-auto">
                  {downloadedFolios?.length} applications downloaded but not
                  received at Creso
                </span>
                <Tooltip>
                  <TooltipTrigger asChild onFocus={(e) => e.preventDefault()}>
                    <Button
                      onClick={() =>
                        handleDownload(downloadedFolios as COBFolio[])
                      }
                      disabled={isDownloading}
                      variant="ghost"
                    >
                      {isDownloading ? (
                        <Loader2 className="animate-spin" />
                      ) : (
                        <Download />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Download again</TooltipContent>
                </Tooltip>
                <ConfirmCobTransit
                  client={client}
                  folioId={downloadedFolios?.map((f) => f.folio)}
                  trigger={
                    <Button variant="ghost" disabled={isDownloading}>
                      <Send />
                      Courier
                    </Button>
                  }
                />
              </div>
            </ConditionalRenderer>
            <div className="flex flex-col border border-border rounded-lg my-4">
              <div className="grid grid-cols-12 p-4 text-sm font-medium bg-card border-b rounded-t-lg border-border">
                <div className="col-span-4 font-semibold">Broker</div>
                <div className="col-span-3 font-semibold text-right">
                  Amount
                </div>
                <div className="col-span-3 font-semibold text-right">
                  Status
                </div>
                <div className="col-span-2" />
              </div>
              {Object.entries(folioDetailsGroupedByBrokerCode).map(
                ([brokerCode, group], idx) => (
                  <BrokerGroup key={`${brokerCode}-${idx}`} group={group} />
                )
              )}
              {client.folio_details.length === 0 && (
                <p className="text-sm text-muted-foreground py-6 text-center">
                  No folio details available.
                </p>
              )}
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
